import { detectFileType } from "./fileTypeDetector";
import { isExtensionValueAllowed, isDangerousExtension } from "../constants/fileValidation";
import logger from "./logger";

/**
 * Resolves file extension by ALWAYS reading magic bytes first.
 *
 * Logic:
 * 1. Detect content type via magic bytes
 * 2. If inferred ext is detected AND is allowed (not dangerous) → return inferred ext
 * 3. If inferred ext is dangerous or not allowed → return undefined (file should have been filtered)
 * 4. If detection returns empty → fall back to filename extension
 * 5. If filename also has no extension → return undefined
 *
 * @param {string} fileName - The file name
 * @param {File|Blob} actualFile - The actual file object for type detection
 * @returns {Promise<string|undefined>} The resolved extension for the API payload
 */
export const resolveFileExtension = async (fileName, actualFile) => {
    const lastDotIndex = fileName.lastIndexOf('.');
    const nameExt = lastDotIndex > 0
        ? fileName.substring(lastDotIndex + 1).toLowerCase()
        : '';

    // Always attempt magic byte detection
    const inferredExt = await detectFileType(actualFile);

    if (inferredExt) {
        // Inferred extension detected — validate before using
        if (isDangerousExtension(inferredExt)) {
            logger.warn(`🚫 Inferred extension "${inferredExt}" is dangerous for file "${fileName}"`);
            return undefined;
        }
        if (!isExtensionValueAllowed(inferredExt)) {
            logger.warn(`🚫 Inferred extension "${inferredExt}" is not in allowed list for file "${fileName}"`);
            return undefined;
        }

        // Log when inferred differs from filename extension
        if (nameExt && nameExt !== inferredExt) {
            logger.info(
                `📄 File "${fileName}": name ext="${nameExt}", inferred ext="${inferredExt}" — using inferred`
            );
        } else if (!nameExt) {
            logger.info(`📄 Detected type "${inferredExt}" for extensionless file: ${fileName}`);
        }

        return inferredExt;
    }

    // Cannot infer — fall back to filename extension
    if (nameExt) {
        logger.debug(`🔍 Could not infer type for "${fileName}", using filename extension: "${nameExt}"`);
        return nameExt;
    }

    // No extension at all
    logger.warn(`⚠️ No extension resolved for "${fileName}" (no magic bytes, no filename extension)`);
    return undefined;
};
